import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Assessment } from 'src/app/models/assessment';
import { Union } from 'src/app/models/union';
import { AssessmentService } from 'src/app/services/assessment.service';
import { TaxService } from 'src/app/services/tax.service';
import { UnionDomain } from 'src/app/services/union.domain';
import { UnionService } from 'src/app/services/union.service';
import { Tax } from '../admin/tax-collection/tax';

@Component({
  selector: 'app-users-status',
  templateUrl: './users-status.component.html',
  styleUrls: ['./users-status.component.scss'],
})
export class UsersStatusComponent implements OnInit, OnDestroy {
  taxList: Tax[] = [];
  private subscriptions: Subscription[] = [];
  qr_code!: string;
  assessment: Assessment = new Assessment();
  unionData!: Union;
  tax: number = 0;
  aday: number = 0;
  totalTax: number = 0;
  isLoading: boolean = true;
  hasError: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private assessmentService: AssessmentService,
    private toastr: ToastrService,
    public unionDomain: UnionDomain,
    private taxService: TaxService,
    private unionService: UnionService
  ) {}

  async ngOnInit(): Promise<void> {
    this.qr_code = this.route.snapshot.queryParams['qr_code'];
    
    if (!this.qr_code) {
      this.hasError = true;
      this.isLoading = false;
      this.toastr.error('QR কোড পাওয়া যায়নি। অনুগ্রহ করে সঠিক লিংক ব্যবহার করুন।');
      return;
    }

    try {
      await this.loadUnionData();
      await this.loadAssessmentData();
      await this.loadTaxData();
    } catch (error) {
      this.hasError = true;
      this.toastr.error('তথ্য লোড করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।');
    } finally {
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private async loadUnionData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.unionService.getSingleUnion().subscribe({
          next: (res) => {
            this.unionData = res;
            resolve();
          },
          error: (err) => {
            console.error('Union data loading error:', err);
            reject(err);
          },
        })
      );
    });
  }

  private async loadAssessmentData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.assessmentService.getSingleAssessmentByUUID(this.qr_code).subscribe({
          next: (res) => {
            this.assessment = res;
            if (!res || !res.name) {
              this.hasError = true;
              this.toastr.error('এই QR কোডের জন্য কোনো তথ্য পাওয়া যায়নি।');
            }
            resolve();
          },
          error: (err) => {
            console.error('Assessment data loading error:', err);
            this.hasError = true;
            this.toastr.error('সম্পত্তির তথ্য লোড করতে ব্যর্থ।');
            reject(err);
          },
        })
      );
    });
  }

  private async loadTaxData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.taxService.getTaxList(this.qr_code).subscribe({
          next: (res: any) => {
            this.taxList = res;
            this.calculateTaxAmounts();
            resolve();
          },
          error: (err: any) => {
            console.error('Tax data loading error:', err);
            this.toastr.error('ট্যাক্স তথ্য লোড করতে ব্যর্থ।');
            reject(err);
          },
        })
      );
    });
  }

  private calculateTaxAmounts(): void {
    this.tax = 0;
    this.aday = 0;
    this.totalTax = 0;

    this.taxList.forEach((taxItem) => {
      if (taxItem.debit_credit === 'C') {
        // Credit - Tax assigned/charged
        this.tax += taxItem.amount;
        this.totalTax += taxItem.amount;
      } else if (taxItem.debit_credit === 'D') {
        // Debit - Tax paid
        this.tax -= taxItem.amount;
        this.aday += taxItem.amount;
      }
    });

    // Ensure no negative values for display
    this.tax = Math.max(0, this.tax);
    this.aday = Math.max(0, this.aday);
    this.totalTax = Math.max(0, this.totalTax);
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  // Get only payment history (debit transactions)
  getPaymentHistory(): Tax[] {
    return this.taxList.filter(tax => tax.debit_credit === 'D');
  }

  // Helper method for template (keeping existing functionality)
  Number(year: string): number {
    return Number(year);
  }
}
